import { SessionTimelineEventInput } from 'types/session';
import {
  SessionStateEvent,
  SessionStateEventType,
} from 'types/sessionStateLog';
import {
  normalizeAnalyticsPath,
  normalizeSessionEventName,
} from '@/lib/Analytics/productEvents';
import { getBrowserContext } from './browserContext';
import { getAppVersion } from './config';

const normalizePath = (value: string): string => {
  if (!value) return value;
  try {
    const parsed = new URL(
      value,
      value.startsWith('http') ? undefined : window.location.origin
    );
    return normalizeAnalyticsPath(parsed.pathname) ?? parsed.pathname;
  } catch {
    const pathname = value.split(/[?#]/)[0];
    return normalizeAnalyticsPath(pathname) ?? pathname;
  }
};

const mapConsoleSeverity = (level?: unknown): SessionStateEvent['severity'] => {
  if (level === 'error') return 'ERROR';
  if (level === 'warn') return 'WARN';
  return 'INFO';
};

const mapStatusSeverity = (status?: unknown): SessionStateEvent['severity'] => {
  if (typeof status === 'number') {
    if (status >= 500) return 'ERROR';
    if (status >= 400) return 'WARN';
  }
  return 'INFO';
};

const stringFrom = (value: unknown) =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const parseSeverity = (
  value: unknown
): SessionStateEvent['severity'] | undefined => {
  if (
    value === 'INFO' ||
    value === 'WARN' ||
    value === 'ERROR' ||
    value === 'FATAL'
  ) {
    return value;
  }
  return undefined;
};

export const mapTimelineEventsToStateEvents = ({
  events,
  sessionId,
  currentUserId,
  assignmentIdOverride,
}: {
  events: SessionTimelineEventInput[];
  sessionId: string;
  currentUserId: string | null | undefined;
  assignmentIdOverride?: string;
}): SessionStateEvent[] =>
  events.map((event) => {
    const baseContext = getBrowserContext({
      assignmentId: assignmentIdOverride,
    });
    const base = {
      sessionId,
      userId: currentUserId ?? undefined,
      clientTimestamp: event.occurredAt,
      platform: 'WEB' as const,
      assignmentId:
        assignmentIdOverride ?? baseContext.assignmentId ?? undefined,
      appVersion: getAppVersion(),
      eventPayload: {
        ...baseContext,
      } as Record<string, unknown>,
    };

    if (event.eventType === 'navigation') {
      const pathname =
        typeof event.data?.pathname === 'string'
          ? normalizePath(event.data.pathname)
          : undefined;
      const eventName = 'Navigation';
      return {
        ...base,
        eventType: SessionStateEventType.USER_FLOW_ACTION,
        eventName,
        severity: 'INFO',
        eventPayload: {
          ...base.eventPayload,
          event_name: eventName,
          action_detail: 'Route change',
          source_context: pathname,
          action_value: pathname,
          expected_next_event: 'SYSTEM_FLOW',
        },
      };
    }

    if (event.eventType === 'api_call') {
      const endpoint =
        typeof event.data?.endpoint === 'string'
          ? normalizePath(event.data.endpoint)
          : 'unknown';
      const method =
        typeof event.data?.method === 'string'
          ? String(event.data.method).toUpperCase()
          : 'GET';
      const statusCode =
        typeof event.data?.statusCode === 'number'
          ? event.data.statusCode
          : undefined;
      const durationMs =
        typeof event.data?.durationMs === 'number'
          ? Math.max(event.data.durationMs, 0)
          : undefined;
      const requestId =
        typeof event.data?.requestId === 'string'
          ? event.data.requestId
          : undefined;
      const eventName = normalizeSessionEventName(`${method} ${endpoint}`);
      return {
        ...base,
        eventType: SessionStateEventType.SYSTEM_FLOW,
        eventName,
        severity: mapStatusSeverity(statusCode),
        eventPayload: {
          ...base.eventPayload,
          event_name: eventName,
          flow_type: 'API_RESPONSE',
          status_code: statusCode,
          request_params: { endpoint, method },
          duration_ms: durationMs,
          request_id: requestId,
          source_context:
            typeof window !== 'undefined'
              ? normalizeAnalyticsPath(window.location.pathname)
              : undefined,
        },
      };
    }

    if (event.eventType === 'user_action') {
      const action = String(event.data?.action ?? 'user_action');
      const eventName = action;
      const metadata =
        event.data?.metadata && typeof event.data.metadata === 'object'
          ? (event.data.metadata as Record<string, unknown>)
          : {};
      const sourceContext =
        stringFrom(metadata.source_context) ??
        (typeof window !== 'undefined' ? window.location.pathname : undefined);
      const actionDetail =
        stringFrom(metadata.action_detail) ?? 'Client action';
      const expectedNext =
        stringFrom(metadata.expected_next_event) ?? 'SYSTEM_FLOW';
      const elementContext =
        metadata.element && typeof metadata.element === 'object'
          ? (metadata.element as Record<string, unknown>)
          : undefined;
      const actionValue =
        metadata.action_value ??
        (elementContext
          ? stringFrom(
              (elementContext.testId as string | undefined) ||
                (elementContext.id as string | undefined) ||
                (elementContext.ariaLabel as string | undefined) ||
                (elementContext.role as string | undefined) ||
                (elementContext.text as string | undefined) ||
                (elementContext.tag as string | undefined)
            )
          : undefined);
      const interactionSource = stringFrom(metadata.interaction_source);
      const actionId =
        stringFrom(metadata.action_id) ??
        stringFrom(metadata.actionId) ??
        action;
      const severity =
        parseSeverity(metadata.severity) ??
        parseSeverity(metadata.level) ??
        'INFO';
      return {
        ...base,
        eventType: SessionStateEventType.USER_FLOW_ACTION,
        eventName: normalizeSessionEventName(eventName),
        severity,
        eventPayload: {
          ...base.eventPayload,
          event_name: normalizeSessionEventName(eventName),
          action_detail: actionDetail,
          source_context:
            normalizeAnalyticsPath(sourceContext) ?? sourceContext,
          action_value: actionValue,
          expected_next_event: expectedNext,
          interaction_source: interactionSource,
          element: elementContext,
          action_id: actionId,
        },
      };
    }

    const message =
      typeof event.data?.message === 'string'
        ? event.data.message
        : event.eventType;
    const severity = mapConsoleSeverity(
      typeof event.data?.level === 'string' ? event.data.level : undefined
    );
    return {
      ...base,
      eventType: SessionStateEventType.USER_FLOW_ACTION,
      eventName: message,
      severity,
      eventPayload: {
        ...base.eventPayload,
        event_name: message,
        action_detail: event.eventType,
      },
    };
  });
